import React, { useState } from 'react';
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  CircularProgress,
  Box,
  alpha,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Users,
  FileText,
  Settings as SettingsIcon,
  MessageSquare,
  Clock,
  CalendarDays,
  MessageSquareText,
  FileCheck,
} from 'lucide-react';
import { LAYOUT } from '../../../constants/layout';

interface SubMenuItem {
  text: string;
  icon?: React.ReactNode;
  path: string;
  className?: string;
}

interface SubMenuProps {
  item: {
    text: string;
    icon: React.ReactNode;
    path: string;
    className?: string;
    subItems?: SubMenuItem[];
  };
  isDrawerOpen: boolean;
  isSelected: boolean;
  isLoading?: boolean;
}

const SubMenu: React.FC<SubMenuProps> = ({ item, isDrawerOpen, isSelected, isLoading = false }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Check if any sub-item is selected
  const isSubItemSelected = item.subItems?.some(subItem => location.pathname === subItem.path);

  // Determine if this menu or any of its children is selected
  const isMenuActive = isSelected || isSubItemSelected;

  const handleClick = () => {
    if (item.subItems && item.subItems.length > 0) {
      setOpen(!open);
    } else {
      navigate(item.path);
    }
  };

  // Function to get appropriate icon based on menu item text
  const getIconForSubItem = (text: string) => {
    const iconSize = 14;

    switch (text.toLowerCase()) {
      case 'users':
        return <Users size={iconSize} />;
      case 'snippets':
        return <FileText size={iconSize} />;
      case 'chat settings':
        return <MessageSquare size={iconSize} />;
      case 'sms settings':
        return <MessageSquareText size={iconSize} />;
      case 'booking settings':
        return <CalendarDays size={iconSize} />;
      case 'working hours':
        return <Clock size={iconSize} />;
      default:
        return <FileCheck size={iconSize} />;
    }
  };

  return (
    <>
      <ListItem
        button
        onClick={handleClick}
        selected={isMenuActive}
        sx={{
          minHeight: 48,
          justifyContent: isDrawerOpen ? 'initial' : 'center',
          px: LAYOUT.SPACING.sm,
          py: 1,
          borderRadius: '24px',
          mb: 0.5,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
        className={item.className}
      >
        <Tooltip title={!isDrawerOpen ? item.text : ''} placement="right">
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isDrawerOpen ? LAYOUT.SPACING.sm : 'auto',
              justifyContent: 'center',
              color: isMenuActive ? theme.palette.primary.main : theme.palette.text.primary,
            }}
          >
            {item.icon}
          </ListItemIcon>
        </Tooltip>

        {isDrawerOpen && (
          <>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                variant: 'body2',
                fontSize: '0.938rem',
                fontWeight: isMenuActive ? 500 : 400,
                color: isMenuActive ? theme.palette.primary.main : theme.palette.text.primary,
              }}
              className="text-semibold"
            />
            {item.subItems && item.subItems.length > 0 && (
              <>
                {open ? (
                  <ChevronDown size={16} color={theme.palette.text.secondary} />
                ) : (
                  <ChevronRight size={16} color={theme.palette.text.secondary} />
                )}
              </>
            )}
          </>
        )}
      </ListItem>

      {item.subItems && (
        <Collapse in={open && isDrawerOpen} timeout="auto" unmountOnExit>
          <div
            style={{
              overflow: 'hidden', // prevents scrollbars
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE/Edge
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, pl: LAYOUT.SPACING.md }}>
                <CircularProgress size={18} />
              </Box>
            ) : (
              <List
                component="div"
                disablePadding
                sx={{
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari
                  },
                  maxHeight: 250,
                  overflowY: 'auto',
                }}
              >
                {item.subItems.map(subItem => {
                  const isSubSelected = location.pathname === subItem.path;
                  const subItemIcon = subItem.icon || getIconForSubItem(subItem.text);

                  return (
                    <ListItem
                      button
                      key={subItem.path}
                      onClick={() => navigate(subItem.path)}
                      selected={isSubSelected}
                      sx={{
                        minHeight: 40,
                        pl: isDrawerOpen ? LAYOUT.SPACING.md : LAYOUT.SPACING.sm,
                        py: 0.75,
                        borderRadius: '20px',
                        mb: 0.5,
                        ml: isDrawerOpen ? LAYOUT.SPACING.md : 0,
                        transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.12),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.18),
                          },
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                      className={subItem.className}
                    >
                      {!isDrawerOpen ? (
                        <Tooltip title={subItem.text} placement="right">
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: 0,
                              justifyContent: 'center',
                              color: isSubSelected
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                              fontSize: '0.875rem',
                            }}
                          >
                            {subItemIcon}
                          </ListItemIcon>
                        </Tooltip>
                      ) : (
                        <>
                          <ListItemIcon
                            sx={{
                              minWidth: 24,
                              mr: LAYOUT.SPACING.sm,
                              color: isSubSelected
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                              fontSize: '0.875rem',
                            }}
                          >
                            {subItemIcon}
                          </ListItemIcon>
                          <ListItemText
                            primary={subItem.text}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontSize: '0.875rem',
                              fontWeight: isSubSelected ? 500 : 400,
                              color: isSubSelected
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                              noWrap: true,
                            }}
                          />
                        </>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </div>
        </Collapse>
      )}
    </>
  );
};

export default SubMenu;
